import React from "react";
import { ChevronDown, ChevronUp, MessageSquare } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { Toggle } from "../toggle";
import { FileItem, type Location as FileLocation } from "@/types/fse";

import { truncateSummary } from "@/components/feature/layout/left-sidebar/helpers";
import { FlattenedItem } from "@/views/chat/types";
import LocationsModal from "@/views/drive/modals/locationsModal";

interface Props {
  item: FlattenedItem & FileItem;
  isSingleFile: boolean;
  parentLocations: FileLocation[];
  handleFileClick: (file: FileItem) => void;
  handleFileSelect: (file: FileItem, isSelected: boolean) => void;
  toggleSummary: (fileId: string) => void;
  expandedSummaries: Set<string>;
  selectedFiles: Set<string>;
  handleAskFile: (fileId: string) => void;
}

export const FileItemComponent: React.FC<Props> = ({
  item,
  isSingleFile,
  parentLocations,
  handleFileClick,
  handleFileSelect,
  toggleSummary,
  expandedSummaries,
  selectedFiles,
  handleAskFile,
}) => {
  const paddingLeft = `${item.depth * 10}px`;

  return (
    <div className="flex flex-col py-2" style={{ paddingLeft }}>
      <div className="flex items-start">
        <div
          className="flex flex-grow cursor-pointer items-start overflow-hidden"
          onClick={() => handleFileClick(item)}
        >
          <DocumentTextIcon className="text-color-text-ui-heading-text mr-2 h-5 w-5 flex-shrink-0" />
          <span className="text-color-text-ui-heading-text line-clamp-2 flex-grow pr-2 text-xs" title={item.name}>
            {item.name}
          </span>
        </div>
        <div className="ml-2 flex-shrink-0">
          <Toggle
            enabled={selectedFiles.has(item.id)}
            onChange={enabled => handleFileSelect(item, enabled)}
            size="xs"
          />
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 py-2">
        <button
          onClick={e => {
            e.stopPropagation();
            toggleSummary(item.id);
          }}
          className="flex items-center rounded border border-solid border-desert-sand-400 bg-ivory-mist-100 px-2 py-1 text-xs text-desert-sand-900 hover:bg-desert-sand-300"
        >
          {expandedSummaries.has(item.id) ? (
            <>
              <ChevronUp size={14} className="mr-1" />
              Hide
            </>
          ) : (
            <>
              <ChevronDown size={14} className="mr-1" />
              Summary
            </>
          )}
        </button>
        <LocationsModal itemName={item.name} locations={parentLocations} />
        {!isSingleFile && (
          <button
            onClick={() => handleAskFile(item.id)}
            className="flex items-center rounded border border-solid border-desert-sand-400 bg-desert-sand-100 px-2 py-1 text-xs text-desert-sand-900 hover:bg-desert-sand-200"
          >
            <MessageSquare size={14} className="mr-1" />
            Q&A
          </button>
        )}
      </div>
      {expandedSummaries.has(item.id) && item.summary && (
        <div className="mt-1 text-sm text-ui-sidebar-text">
          <ReactMarkdown
            className="prose prose-sm max-w-none"
            components={{
              h1: ({ ...props }) => <h1 className="text-base font-bold" {...props} />,
              h2: ({ ...props }) => <h2 className="text-sm font-semibold" {...props} />,
              h3: ({ ...props }) => <h3 className="text-xs font-semibold" {...props} />,
              p: ({ ...props }) => <p className="text-xs leading-relaxed" {...props} />,
              ul: ({ ...props }) => <ul className="list-inside list-disc pl-4 text-xs" {...props} />,
              ol: ({ ...props }) => <ol className="list-inside list-decimal pl-4 text-xs" {...props} />,
              li: ({ ...props }) => <li className="mb-1" {...props} />,
              strong: ({ ...props }) => <strong className="font-bold" {...props} />,
              em: ({ ...props }) => <em className="italic" {...props} />,
            }}
          >
            {truncateSummary(item.summary)}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};
